import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Spinner from "@/components/ui/spinner";
import { useAuthStoreShallow } from "@/features/authentication";
import { handleAsyncOperation } from "@/shared";
import { useCreateSlideMutation } from "@api/slides-library";
import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import { FoldersSelect } from "@modules/slides-library/features/folders";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import {
  useCallback,
  useEffect,
  useState,
  type KeyboardEventHandler,
} from "react";
import { toast } from "react-toastify";

const DIALOG_ID = "save-to-my-slides";

const SlideSaveDialog = ({
  slide,
  title,
}: {
  slide: StaticEntityType;
  title?: string;
}) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(title || "");
  const [createSlide, { isLoading }] = useCreateSlideMutation();
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const [folder, setFolder] = useState<{ title: string; id: string }>({
    title: "My slides",
    id: "user-slides",
  });

  const handleClose = useCallback(() => {
    dispatch(closeDialog("save-to-my-slides"));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading || !name?.trim()) return;

    handleAsyncOperation({
      operation: async () => {
        await createSlide({ title: name, slide, folderId: folder.id }).unwrap();
        toast.success(`Slide saved to "${folder.title}"`);
        handleClose();
      },
      onError: () => toast.error("Failed to save slide"),
    });
  }, [createSlide, folder.id, folder.title, handleClose, slide, isLoading, name]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.isComposing && name) {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [name, handleSubmit]);

  return (
    <DialogBase>
      <DialogBase.Header title="Save slide" onClose={handleClose} />
      <DialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Slide Name
        </span>
        <Input
          placeholder="Add your slide's name here..."
          value={name}
          onKeyDown={handleKeyDown}
          onChange={(e) => setName(e.target.value)}
          className="tw-mb-5"
        />
        <FoldersSelect folder={folder} setFolder={setFolder} disableTeamSlides={isUserOrCreatorLite} />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save slide"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideSaveDialog.id = DIALOG_ID;
export { SlideSaveDialog };
